// frontend/src/components/events/MusicSelector.tsx

import React, { useState, useRef, useEffect } from 'react';
import SparkButton from '../common/SparkButton';
import './MusicSelector.css';

interface MusicOption {
  id: string;
  name: string;
  artist: string;
  url: string;
  duration: string;
}

interface MusicSelectorProps {
  templateId: string | null;
  selectedMusicId: string | null;
  onSelectMusic: (musicId: string, musicUrl: string, musicName: string) => void;
}

// Define music libraries for each template
export const musicLibraries: Record<string, MusicOption[]> = {
  // Sweets theme music
  'template-candySweetsTemplate': [
    {
        id: 'sweets-candy-clouds',
        name: 'Candy Clouds',
        artist: 'Sparky Sam',
        url: '/themes/sweets/music/candy-clouds.mp3',
        duration: ''          // duration optional – leave blank
    },
    {
        id: 'sweets-electric-whimsey',
        name: 'Electric Whimsey',
        artist: 'Sparky Sam',
        url: '/themes/sweets/music/electric-whimsey.mp3',
        duration: ''
    },
    {
        id: 'sweets-sugar-rush',
        name: 'Sugar Rush',
        artist: 'Sparky Sam',
        url: '/themes/sweets/music/sugar-rush.mp3',
        duration: ''
    },
    {
        id: 'sweets-weve-got-the-candles',
        name: "We've Got the Candles",
        artist: 'Sparky Sam',
        url: "/themes/sweets/music/we've-got-the-candles.mp3",
        duration: ''
    },
    {
        id: 'sweets-lollipop-lights',
        name: "Lollipop Lights",
        artist: 'Sparky Sam',
        url: "/themes/sweets/music/lollipoplights.mp3",
        duration: ''
    },
    {
        id: 'sweets-sprinkle-confetti',
        name: "Spinkle Confetti",
        artist: 'Sparky Sam',
        url: "/themes/sweets/music/sprinkleconfetti.mp3",
        duration: ''
    },
  ],

  // Candy theme music
  'template-candyTheme': [
    {
      id: 'candy-candy-clouds',
      name: 'Candy Clouds',
      artist: 'Sparky Sam',
      url: '/themes/candy/music/candy-clouds.mp3',
      duration: ''          // optional – leave blank
    },
    {
      id: 'candy-electric-whimsey',
      name: 'Electric Whimsey',
      artist: 'Sparky Sam',
      url: '/themes/candy/music/electric-whimsey.mp3',
      duration: ''
    },
    {
      id: 'candy-sugar-dreams',
      name: 'Sugar Dreams',
      artist: 'Sparky Sam',
      url: '/themes/candy/music/sugar-dreams.mp3',
      duration: ''
    },
    {
      id: 'candy-sugar-rush',
      name: 'Sugar Rush',
      artist: 'Sparky Sam',
      url: '/themes/candy/music/sugar-rush.mp3',
      duration: ''
    },
    {
      id: 'candy-weve-got-the-candles',
      name: "We've Got the Candles",
      artist: 'Sparky Sam',
      url: "/themes/candy/music/we've-got-the-candles.mp3",
      duration: ''
    },
    {
        id: 'sweets-lollipop-lights',
        name: "Lollipop Lights",
        artist: 'Sparky Sam',
        url: "/themes/sweets/music/lollipoplights.mp3",
        duration: ''
    },
    {
        id: 'sweets-sprinkle-confetti',
        name: "Spinkle Confetti",
        artist: 'Sparky Sam',
        url: "/themes/sweets/music/sprinkleconfetti.mp3",
        duration: ''
    },
  ],

  // Princess theme music
  'template-princessTheme': [
    {
      id: 'princess-castle-of-icing',
      name: 'Castle of Icing',
      artist: 'Sparky Sam',
      url: '/themes/princess/music/castle-of-icing.mp3',
      duration: ''
    },
    {
      id: 'princess-crown-of-dreams',
      name: 'Crown of Dreams',
      artist: 'Sparky Sam',
      url: '/themes/princess/music/crown-of-dreams.mp3',
      duration: ''
    },
    {
      id: 'princess-bippity-bop',
      name: 'Bippity Bop',
      artist: 'Sparky Sam',
      url: '/themes/princess/music/bippity-bop.mp3',
      duration: ''
    },
    {
      id: 'princess-little-lights',
      name: 'Little Lights',
      artist: 'Sparky Sam',
      url: '/themes/princess/music/little-lights.mp3',
      duration: ''
    }
  ],

  // Race Cars theme music
  'template-raceCarsTheme': [
    {
      id: 'racecars-full-throttle',
      name: 'Full Throttle',
      artist: 'Sparky Sam',
      url: '/themes/racecars/music/fullthrottle.mp3',
      duration: ''
    },
    {
      id: 'racecars-race-face',
      name: 'Race Face',
      artist: 'Sparky Sam',
      url: '/themes/racecars/music/raceface.mp3',
      duration: ''
    },
    {
      id: 'racecars-rev-it-up',
      name: 'Rev It Up',
      artist: 'Sparky Sam',
      url: '/themes/racecars/music/revitup.mp3',
      duration: ''
    },
    {
      id: 'racecars-speed-of-cake',
      name: 'Speed of Cake',
      artist: 'Sparky Sam',
      url: '/themes/racecars/music/speedofcake.mp3',
      duration: ''
    },
    {
      id: 'racecars-fast-lane',
      name: 'Fast Lane',
      artist: 'Sparky Sam',
      url: '/themes/racecars/music/fastlane.mp3',
      duration: ''
    }
  ],

  // Space theme music
  'template-spaceTheme': [
    {
      id: 'space-rocket-roar',
      name: 'Rocket Roar',
      artist: 'Sparky Sam',
      url: '/themes/space/music/rocketroar.mp3',
      duration: ''
    },
    {
      id: 'space-electric-whimsey',
      name: 'Electric Whimsey',
      artist: 'Sparky Sam',
      url: '/themes/space/music/electric-whimsey.mp3',
      duration: ''
    },
    {
      id: 'space-galactic-celebration',
      name: 'Galactic Celebration',
      artist: 'Sparky Sam',
      url: '/themes/space/music/galactic-celebration.mp3',
      duration: ''
    },
    {
      id: 'space-streamers-of-stardust',
      name: 'Streamers of Stardust',
      artist: 'Sparky Sam',
      url: '/themes/space/music/streamersofstardust.mp3',
      duration: ''
    },
    {
      id: 'space-light-up-the-sky',
      name: 'Light Up the Sky',
      artist: 'Sparky Sam',
      url: '/themes/space/music/lightupthesky.mp3',
      duration: ''
    },
    {
      id: 'space-cosmic-parade',
      name: 'Cosmic Parade',
      artist: 'Sparky Sam',
      url: '/themes/space/music/cosmicparade.mp3',
      duration: ''
    },
  ],
  // Dinosaurs theme music (existing + new additions)
  'template-dinosaursTheme': [
    {
      id: 'dinosaurs-dino-rock',
      name: 'Dino Rock',
      artist: 'Spark Sam',
      url: '/themes/dinosaurs/music/dino-rock.mp3',
      duration: ''  // leave blank if unknown
    },
    {
      id: 'dinosaurs-dino-bop',
      name: 'Dino Bop',
      artist: 'Spark Sam',
      url: '/themes/dinosaurs/music/dino-bop.mp3',
      duration: ''
    },
    {
      id: 'dinosaurs-jurassic-dreams',
      name: 'Jurassic Dreams',
      artist: 'Spark Sam',
      url: '/themes/dinosaurs/music/jurassic-dreams.mp3',
      duration: ''
    },
    {
      id: 'dinosaurs-sing-o-saurus',
      name: 'Sing-o-saurus',
      artist: 'Spark Sam',
      url: '/themes/dinosaurs/music/sing-o-saurus.mp3',
      duration: ''
    },
    {
      id: 'dinosaurs-stomp-the-beat',
      name: 'Stomp the Beat',
      artist: 'Spark Sam',
      url: '/themes/dinosaurs/music/stompthebeat.mp3',
      duration: ''
    },
    {
      id: 'dinosaurs-diplodocus-dip',
      name: 'Diplodocus Dip',
      artist: 'Spark Sam',
      url: '/themes/dinosaurs/music/diplodocusdip.mp3',
      duration: ''
    },
    {
      id: 'dinosaurs-dino-disaster',
      name: 'Dino Disaster',
      artist: 'Spark Sam',
      url: '/themes/dinosaurs/music/dinodisaster.mp3',
      duration: ''
    }
  ],
  // Construction theme music
  'template-constructionTheme': [
    {
      id: 'construction-brick-by-brick',
      name: 'Brick by Brick',
      artist: 'Sparky Sam',
      url: '/themes/construction/music/brickbybrick.mp3',
      duration: ''
    },
    {
      id: 'construction-build-it-up',
      name: 'Build It Up',
      artist: 'Sparky Sam',
      url: '/themes/construction/music/builditup.mp3',
      duration: ''
    },
    {
      id: 'construction-concrete-cake',
      name: 'Concrete Cake',
      artist: 'Sparky Sam',
      url: '/themes/construction/music/concretecake.mp3',
      duration: ''
    },
    {
      id: 'construction-hardhats-and-heartbeats',
      name: 'Hardhats and Heartbeats',
      artist: 'Sparky Sam',
      url: '/themes/construction/music/hardhatsandheartbeats.mp3',
      duration: ''
    },
    {
      id: 'construction-stacking-dreams',
      name: 'Stacking Dreams',
      artist: 'Sparky Sam',
      url: '/themes/construction/music/stackingdreams.mp3',
      duration: ''
    },
    {
      id: 'construction-wrecking-ball',
      name: 'Wrecking Ball',
      artist: 'Sparky Sam',
      url: '/themes/construction/music/wreckingball.mp3',
      duration: ''
    }
  ],
  // ✨ Unicorn theme music
'template-unicornTheme': [
  {
    id: 'unicorn-cake-with-a-horn',
    name: 'Cake with a Horn',
    artist: 'Spark Sam',
    url: '/themes/unicorn/music/cakewithahorn.mp3',
    duration: ''          // leave blank if unknown
  },
  {
    id: 'unicorn-electric-whimsey',
    name: 'Electric Whimsey',
    artist: 'Spark Sam',
    url: '/themes/unicorn/music/electric-whimsey.mp3',
    duration: ''
  },
  {
    id: 'unicorn-rainbow-candles',
    name: 'Rainbow Candles',
    artist: 'Spark Sam',
    url: '/themes/unicorn/music/rainbowcandles.mp3',
    duration: ''
  },
  {
    id: 'unicorn-unicorn-bash',
    name: 'Unicorn Bash',
    artist: 'Spark Sam',
    url: '/themes/unicorn/music/unicornbash.mp3',
    duration: ''
  },
  {
    id: 'unicorn-unicorn-dreams',
    name: 'Unicorn Dreams',
    artist: 'Spark Sam',
    url: '/themes/unicorn/music/unicorndreams.mp3',
    duration: ''
  },
  {
    id: 'unicorn-unicorn-house',
    name: 'Unicorn House',
    artist: 'Spark Sam',
    url: '/themes/unicorn/music/unicornhouse.mp3',
    duration: ''
  }
],
// 🏀 Bounce theme music
'template-bounceTheme': [
  {
    id: 'bounce-123-bounce',
    name: '123 Bounce',
    artist: 'Spark Sam',
    url: '/themes/bounce/music/123bounce.mp3',
    duration: ''          // leave blank if unknown
  },
  {
    id: 'bounce-all-that-bounce',
    name: 'All That Bounce',
    artist: 'Spark Sam',
    url: '/themes/bounce/music/allthatbounce.mp3',
    duration: ''          // leave blank if unknown
  },
  {
    id: 'bounce-to-the-sky',
    name: 'To The Sky',
    artist: 'Spark Sam',
    url: '/themes/bounce/music/tothesky.mp3',
    duration: ''          // leave blank if unknown
  },
  {
    id: 'bounce-bounce-to-the-rhythm',
    name: 'Bounce to the Rhythm',
    artist: 'Spark Sam',
    url: '/themes/bounce/music/bouncetotherhythm.mp3',
    duration: ''
  },
  {
    id: 'bounce-bounce-into-the-night',
    name: 'Bounce Into the Night',
    artist: 'Spark Sam',
    url: '/themes/bounce/music/bounceintothenight.mp3',
    duration: ''
  },
  {
    id: 'bounce-make-the-party-bounce',
    name: 'Make the Party Bounce',
    artist: 'Spark Sam',
    url: '/themes/bounce/music/makethepartybounce.mp3',
    duration: ''
  },
  {
    id: 'bounce-room-is-alive',
    name: 'Room Is Alive',
    artist: 'Spark Sam',
    url: '/themes/bounce/music/roomisalive.mp3',
    duration: ''
  }
],
// 🤸‍♀️ Gymnastics theme music
'template-gymnasticsTheme': [
  {
    id: 'gymnastics-cartwheels-and-backflips',
    name: 'Cartwheels & Backflips',
    artist: 'Spark Sam',
    url: '/themes/gymnastics/music/cartwheelsandbackflips.mp3',
    duration: ''          // leave blank if unknown
  },
  {
    id: 'gymnastics-dreams-that-soar',
    name: 'Dreams That Soar',
    artist: 'Spark Sam',
    url: '/themes/gymnastics/music/dreamsthatsoar.mp3',
    duration: ''
  },
  {
    id: 'gymnastics-laughter-flows',
    name: 'Laughter Flows',
    artist: 'Spark Sam',
    url: '/themes/gymnastics/music/laughterflows.mp3',
    duration: ''
  },
  {
    id: 'gymnastics-party-flip',
    name: 'Party Flip',
    artist: 'Spark Sam',
    url: '/themes/gymnastics/music/partyflip.mp3',
    duration: ''
  }
],
'template-bowlingTheme': [
  {
    id: 'bowling-in-the-alley',
    name: 'In the Alley',
    artist: 'Spark Sam',
    url: '/themes/bowling/music/inthealley.mp3',
    duration: ''
  },
  {
    id: 'bowling-lanes-are-lit',
    name: 'Lanes Are Lit',
    artist: 'Spark Sam',
    url: '/themes/bowling/music/lanesarelit.mp3',
    duration: ''
  },
  {
    id: 'bowling-moonlight-on-the-floor',
    name: 'Moonlight on the Floor',
    artist: 'Spark Sam',
    url: '/themes/bowling/music/moonlightonthefloor.mp3',
    duration: ''
  },
  {
    id: 'bowling-the-bowling-song',
    name: 'The Bowling Song',
    artist: 'Spark Sam',
    url: '/themes/bowling/music/thwbowlingsong.mp3',
    duration: ''
  },
  {
    id: 'bowling-strike-up-the-fun',
    name: 'Strike Up the Fun',
    artist: 'Spark Sam',
    url: '/themes/bowling/music/strikeupthefun.mp3',
    duration: ''
  },
  {
    id: 'bowling-strike-it-loud',
    name: 'Strike It Loud',
    artist: 'Spark Sam',
    url: '/themes/bowling/music/strikeitloud.mp3',
    duration: ''
  }
],
/* ─────────── OCEAN THEME ─────────── */
'template-oceanTheme': [
  {
    id: 'ocean-seashell-candles',
    name: 'Seashell Candles',
    artist: 'Spark Sam',
    url: '/themes/ocean/music/seashellcandles.mp3',
    duration: ''
  },
  {
    id: 'ocean-sun-dip-slow',
    name: 'Sun Dip Slow',
    artist: 'Spark Sam',
    url: '/themes/ocean/music/sundipslow.mp3',
    duration: ''
  },
  {
    id: 'ocean-waves-of-joy',
    name: 'Waves of Joy',
    artist: 'Spark Sam',
    url: '/themes/ocean/music/wavesofjoy.mp3',
    duration: ''
  }
],

/* ─────────── DANCE THEME ─────────── */
'template-danceTheme': [
  {
    id: 'dance-birthday-groove',
    name: 'Birthday Groove',
    artist: 'Spark Sam',
    url: '/themes/dance/music/birthdaygroove.mp3',
    duration: ''
  },
  {
    id: 'dance-dance-all-night',
    name: 'Dance All Night',
    artist: 'Spark Sam',
    url: '/themes/dance/music/danceallnight.mp3',
    duration: ''
  },
  {
    id: 'dance-nights-alive',
    name: 'Nights Alive',
    artist: 'Spark Sam',
    url: '/themes/dance/music/nightsalive.mp3',
    duration: ''
  },
  {
    id: 'dance-party-son',
    name: 'Party On',
    artist: 'Spark Sam',
    url: '/themes/dance/music/partyson.mp3',
    duration: ''
  },
  {
    id: 'dance-rhythm-climb',
    name: 'Rhythm Climb',
    artist: 'Spark Sam',
    url: '/themes/dance/music/rhythmclimb.mp3',
    duration: ''
  },
  {
    id: 'space-electric-whimsey',
    name: 'Electric Whimsey',
    artist: 'Sparky Sam',
    url: '/themes/space/music/electric-whimsey.mp3',
    duration: ''
  },
  {
    id: 'dance-make-it-count',
    name: 'Make it Count',
    artist: 'Sparky Sam',
    url: '/themes/space/music/makeitcount.mp3',
    duration: ''
  },
],

/* ───────── GO‑KART THEME ────────── */
'template-gokartTheme': [
  {
    id: 'gokart-burn-the-rubber',
    name: 'Burn the Rubber',
    artist: 'Spark Sam',
    url: '/themes/gokart/music/burntherubber.mp3',
    duration: ''
  },
  {
    id: 'gokart-rush',
    name: 'Rush',
    artist: 'Spark Sam',
    url: '/themes/gokart/music/rush.mp3',
    duration: ''
  },
  {
    id: 'gokart-screech',
    name: 'Screech',
    artist: 'Spark Sam',
    url: '/themes/gokart/music/screech.mp3',
    duration: ''
  },
  {
    id: 'gokart-fast-lane',
    name: 'Fast Lane',
    artist: 'Spark Sam',
    url: '/themes/gokart/music/fastlane.mp3',
    duration: ''
  }
],

/* ───────── FOOTBALL THEME ───────── */
'template-footballTheme': [
  {
    id: 'football-bash-the-gridiron',
    name: 'Bash the Gridiron',
    artist: 'Spark Sam',
    url: '/themes/football/music/bashthegridiron.mp3',
    duration: ''
  },
  {
    id: 'football-birthday-blitz',
    name: 'Birthday Blitz',
    artist: 'Spark Sam',
    url: '/themes/football/music/birthdayblitz.mp3',
    duration: ''
  },
  {
    id: 'football-candles-light-the-field',
    name: 'Candles Light the Field',
    artist: 'Spark Sam',
    url: '/themes/football/music/candleslightthefield.mp3',
    duration: ''
  },
  {
    id: 'football-endzone-dance',
    name: 'Endzone Dance',
    artist: 'Spark Sam',
    url: '/themes/football/music/endzonedance.mp3',
    duration: ''
  },
  {
    id: 'football-lets-win',
    name: "Let's Win",
    artist: 'Spark Sam',
    url: '/themes/football/music/letswin.mp3',
    duration: ''
  },
  {
    id: 'football-pigskin-dreams',
    name: 'Pigskin Dreams',
    artist: 'Spark Sam',
    url: '/themes/football/music/pigskindreams.mp3',
    duration: ''
  },
  {
    id: 'football-touchdown-celebration',
    name: 'Touchdown Celebration',
    artist: 'Spark Sam',
    url: '/themes/football/music/touchdowncelebration.mp3',
    duration: ''
  },
  {
    id: 'football-yeah-that-football',
    name: 'Yeah That Football',
    artist: 'Spark Sam',
    url: '/themes/football/music/yeathatfootball.mp3',
    duration: ''
  }
],

/* ───────── TRAMPOLINE THEME ─────── */
'template-trampolineTheme': [
  {
    id: 'trampoline-beats-bounce',
    name: 'Beats Bounce',
    artist: 'Spark Sam',
    url: '/themes/trampoline/music/beatsbounce.mp3',
    duration: ''
  },
  {
    id: 'trampoline-bounce-with-me',
    name: 'Bounce With Me',
    artist: 'Spark Sam',
    url: '/themes/trampoline/music/bouncewithme.mp3',
    duration: ''
  },
  {
    id: 'trampoline-flying-so-free',
    name: 'Flying So Free',
    artist: 'Spark Sam',
    url: '/themes/trampoline/music/flyingsofree.mp3',
    duration: ''
  },
  {
    id: 'trampoline-partys-jumpin',
    name: "Party's Jumpin",
    artist: 'Spark Sam',
    url: '/themes/trampoline/music/partysjumpin.mp3',
    duration: ''
  },
  {
    id: 'trampoline-rhythm-bounce',
    name: 'Rhythm Bounce',
    artist: 'Spark Sam',
    url: '/themes/trampoline/music/rhythmbounce.mp3',
    duration: ''
  },
  {
    id: 'trampoline-touch-the-sky',
    name: 'Touch the Sky',
    artist: 'Spark Sam',
    url: '/themes/trampoline/music/touchthesky.mp3',
    duration: ''
  },
  {
    id: 'trampoline-trampoline-calling',
    name: 'Trampoline Calling',
    artist: 'Spark Sam',
    url: '/themes/trampoline/music/trampolinecalling.mp3',
    duration: ''
  }
],

/* ───────── SAFARI THEME ─────────── */
'template-safariTheme': [
  {
    id: 'safari-lion-roar',
    name: 'Lion Roar',
    artist: 'Spark Sam',
    url: '/themes/safari/music/lionroar.mp3',
    duration: ''
  },
  {
    id: 'safari-lions-and-monkeys-and-cake',
    name: 'Lions and Monkeys and Cake',
    artist: 'Spark Sam',
    url: '/themes/safari/music/lionsandmonkeysandcake.mp3',
    duration: ''
  },
  {
    id: 'safari-party-is-king',
    name: 'Party Is King',
    artist: 'Spark Sam',
    url: '/themes/safari/music/partyisking.mp3',
    duration: ''
  },
  {
    id: 'safari-tiger-skee-bop',
    name: 'Tiger‑Skee‑Bop',
    artist: 'Spark Sam',
    url: '/themes/safari/music/tiger-skee-bop.mp3',
    duration: ''
  },
  {
    id: 'safari-wild-hearts',
    name: 'Wild Hearts',
    artist: 'Spark Sam',
    url: '/themes/safari/music/wildhearts.mp3',
    duration: ''
  },
  {
    id: 'safari-zebra-dance',
    name: 'Zebra Dance',
    artist: 'Spark Sam',
    url: '/themes/safari/music/zebradance.mp3',
    duration: ''
  }
],

/* ───────── POOL PARTY THEME ─────── */
'template-poolPartyTheme': [
  {
    id: 'poolparty-birthday-by-the-pool',
    name: 'Birthday by the Pool',
    artist: 'Spark Sam',
    url: '/themes/poolparty/music/birthdaybythepool.mp3',
    duration: ''
  },
  {
    id: 'poolparty-cannonball',
    name: 'Cannonball!',
    artist: 'Spark Sam',
    url: '/themes/poolparty/music/cannonball!.mp3',
    duration: ''
  },
  {
    id: 'poolparty-poolside-groove',
    name: 'Poolside Groove',
    artist: 'Spark Sam',
    url: '/themes/poolparty/music/poolsidegroove.mp3',
    duration: ''
  },
  {
    id: 'poolparty-poolside-vibes',
    name: 'Poolside Vibes',
    artist: 'Spark Sam',
    url: '/themes/poolparty/music/poolsidevibes.mp3',
    duration: ''
  },
  {
    id: 'poolparty-sunburnt-skies',
    name: 'Sunburnt Skies',
    artist: 'Spark Sam',
    url: '/themes/poolparty/music/sunburntskies.mp3',
    duration: ''
  },
  {
    id: 'poolparty-sun-is-bright',
    name: 'Sun Is Bright',
    artist: 'Spark Sam',
    url: '/themes/poolparty/music/sunisbright.mp3',
    duration: ''
  }
],
/* ───────── POOL PARTY THEME ─────── */
'template-poolpartycinematicTheme': [
  {
    id: 'poolparty-cannonball',
    name: 'Cannonball!',
    artist: 'Spark Sam',
    url: '/themes/poolparty/music/cannonball!.mp3',
    duration: ''
  },
  {
    id: 'poolparty-birthday-by-the-pool',
    name: 'Birthday by the Pool',
    artist: 'Spark Sam',
    url: '/themes/poolparty/music/birthdaybythepool.mp3',
    duration: ''
  },
  {
    id: 'poolparty-poolside-groove',
    name: 'Poolside Groove',
    artist: 'Spark Sam',
    url: '/themes/poolparty/music/poolsidegroove.mp3',
    duration: ''
  },
  {
    id: 'poolparty-poolside-vibes',
    name: 'Poolside Vibes',
    artist: 'Spark Sam',
    url: '/themes/poolparty/music/poolsidevibes.mp3',
    duration: ''
  },
  {
    id: 'poolparty-sunburnt-skies',
    name: 'Sunburnt Skies',
    artist: 'Spark Sam',
    url: '/themes/poolparty/music/sunburntskies.mp3',
    duration: ''
  },
  {
    id: 'poolparty-sun-is-bright',
    name: 'Sun Is Bright',
    artist: 'Spark Sam',
    url: '/themes/poolparty/music/sunisbright.mp3',
    duration: ''
  }
],

/* ───────── VIDEO GAME THEME ─────── */
'template-videoGameTheme': [
  {
    id: 'videogame-block-party',
    name: 'Block Party',
    artist: 'Spark Sam',
    url: '/themes/videogame/music/blockparty.mp3',
    duration: ''
  },
  {
    id: 'videogame-cake-on-fire',
    name: 'Cake on Fire',
    artist: 'Spark Sam',
    url: '/themes/videogame/music/cakeonfire.mp3',
    duration: ''
  },
  {
    id: 'videogame-level-up',
    name: 'Level Up',
    artist: 'Spark Sam',
    url: '/themes/videogame/music/levelup.mp3',
    duration: ''
  },
  {
    id: 'videogame-pixel-party',
    name: 'Pixel Party',
    artist: 'Spark Sam',
    url: '/themes/videogame/music/pixelparty.mp3',
    duration: ''
  },
  {
    id: 'videogame-power-up',
    name: 'Power Up',
    artist: 'Spark Sam',
    url: '/themes/videogame/music/powerup.mp3',
    duration: ''
  }
],

/* ───────── MONSTERS THEME ───────── */
'template-monstersTheme': [
  {
    id: 'monsters-freaking-out',
    name: 'Freaking Out',
    artist: 'Spark Sam',
    url: '/themes/monsters/music/freakingout.mp3',
    duration: ''
  },
  {
    id: 'monsters-midnight-stage',
    name: 'Midnight Stage',
    artist: 'Spark Sam',
    url: '/themes/monsters/music/midnightstage.mp3',
    duration: ''
  },
  {
    id: 'monsters-monster-bash',
    name: 'Monster Bash',
    artist: 'Spark Sam',
    url: '/themes/monsters/music/monsterbash.mp3',
    duration: ''
  },
  {
    id: 'monsters-moonlight-glows',
    name: 'Moonlight Glows',
    artist: 'Spark Sam',
    url: '/themes/monsters/music/moonlightglows.mp3',
    duration: ''
  },
  {
    id: 'monsters-party-wild',
    name: 'Party Wild',
    artist: 'Spark Sam',
    url: '/themes/monsters/music/partywild.mp3',
    duration: ''
  },
  {
    id: 'monsters-werewolf-howl',
    name: 'Werewolf Howl',
    artist: 'Spark Sam',
    url: '/themes/monsters/music/werewolfhowl.mp3',
    duration: ''
  }
],

/* ───────── MINECRAFT THEME ──────── */
'template-minecraftTheme': [
  {
    id: 'minecraft-cake-on-fire',
    name: 'Cake on Fire',
    artist: 'Spark Sam',
    url: '/themes/minecraft/music/cakeonfire.mp3',
    duration: ''
  },
  {
    id: 'minecraft-block-party',
    name: 'Block Party',
    artist: 'Spark Sam',
    url: '/themes/minecraft/music/blockparty.mp3',
    duration: ''
  },
  {
    id: 'minecraft-level-up',
    name: 'Level Up',
    artist: 'Spark Sam',
    url: '/themes/minecraft/music/levelup.mp3',
    duration: ''
  },
  {
    id: 'minecraft-pixel-party',
    name: 'Pixel Party',
    artist: 'Spark Sam',
    url: '/themes/minecraft/music/pixelparty.mp3',
    duration: ''
  },
  {
    id: 'minecraft-power-up',
    name: 'Power Up',
    artist: 'Spark Sam',
    url: '/themes/minecraft/music/powerup.mp3',
    duration: ''
  }
],

/* ───────── ICE CREAM THEME ──────── */
'template-icecreamTheme': [
  {
    id: 'icecream-scoop-the-fun',
    name: 'Scoop the Fun',
    artist: 'Spark Sam',
    url: '/themes/icecream/music/scoopthefun1.mp3',
    duration: ''
  },
  {
    id: 'icecream-ice-cream-bop',
    name: 'Ice Cream Bop',
    artist: 'Spark Sam',
    url: '/themes/icecream/music/icecreambop.mp3',
    duration: ''
  },
  {
    id: 'icecream-melting-on-my-tongue',
    name: 'Melting on My Tongue',
    artist: 'Spark Sam',
    url: '/themes/icecream/music/meltingonmytongue.mp3',
    duration: ''
  },
  {
    id: 'icecream-sprinkler-rain',
    name: 'Sprinkler Rain',
    artist: 'Spark Sam',
    url: '/themes/icecream/music/sprinklerain1.mp3',
    duration: ''
  },
  {
    id: 'icecream-sweet-like-ice-cream',
    name: 'Sweet Like Ice Cream',
    artist: 'Spark Sam',
    url: '/themes/icecream/music/sweetlikeicecream1.mp3',
    duration: ''
  }
],

/* ───────── BASKETBALL THEME ─────── */
'template-basketballTheme': [
  {
    id: 'basketball-birthday-bounce',
    name: 'Birthday Bounce',
    artist: 'Spark Sam',
    url: '/themes/basketball/music/birthdaybounce.mp3',
    duration: ''
  },
  {
    id: 'basketball-cake-bounce',
    name: 'Cake Bounce',
    artist: 'Spark Sam',
    url: '/themes/basketball/music/cakebounce.mp3',
    duration: ''
  },
  {
    id: 'basketball-cakes-and-dunks',
    name: 'Cakes and Dunks',
    artist: 'Spark Sam',
    url: '/themes/basketball/music/cakesanddunks.mp3',
    duration: ''
  },
  {
    id: 'basketball-dribble-dunk-laugh',
    name: 'Dribble Dunk Laugh',
    artist: 'Spark Sam',
    url: '/themes/basketball/music/dribbledunklaugh.mp3',
    duration: ''
  },
  {
    id: 'basketball-floorboards-jump',
    name: 'Floorboards Jump',
    artist: 'Spark Sam',
    url: '/themes/basketball/music/floorboardsjump.mp3',
    duration: ''
  },
  {
    id: 'basketball-hoop-dreams',
    name: 'Hoop Dreams',
    artist: 'Spark Sam',
    url: '/themes/basketball/music/hoopdreams.mp3',
    duration: ''
  }
],

/* ───────── ART THEME ────────────── */
'template-artTheme': [
  {
    id: 'art-confetti-in-disguise',
    name: 'Confetti in Disguise',
    artist: 'Spark Sam',
    url: '/themes/art/music/confettiindisguise.mp3',
    duration: ''
  },
  {
    id: 'art-birthday-all-for-you',
    name: 'Birthday All for You',
    artist: 'Spark Sam',
    url: '/themes/art/music/birthdayallforyou.mp3',
    duration: ''
  },
  {
    id: 'art-canvas-of-us',
    name: 'Canvas of Us',
    artist: 'Spark Sam',
    url: '/themes/art/music/canvasofus.mp3',
    duration: ''
  },
  {
    id: 'art-leave-its-mark',
    name: 'Leave Its Mark',
    artist: 'Spark Sam',
    url: '/themes/art/music/leaveitsmark.mp3',
    duration: ''
  },
  {
    id: 'art-painted-days',
    name: 'Painted Days',
    artist: 'Spark Sam',
    url: '/themes/art/music/painteddays.mp3',
    duration: ''
  },
  {
    id: 'art-paint-the-night',
    name: 'Paint the Night',
    artist: 'Spark Sam',
    url: '/themes/art/music/paintthenight.mp3',
    duration: ''
  },
  {
    id: 'art-canvas-of-groove',
    name: 'Canvas of Groove',
    artist: 'Spark Sam',
    url: '/themes/art/music/canvasofgroove.mp3',
    duration: ''
  }
],
/* ───────── SUPER‑HERO THEME ─────── */
'template-superHeroTheme': [
  {
    id: 'superhero-act-like-me',
    name: 'Act Like Me',
    artist: 'Spark Sam',
    url: '/themes/superheros/music/actlikeme.mp3',
    duration: ''
  },
  {
    id: 'superhero-battles-in-the-rain',
    name: 'Battles in the Rain',
    artist: 'Spark Sam',
    url: '/themes/superheros/music/battlesintherain.mp3',
    duration: ''
  },
  {
    id: 'superhero-masks-and-capes',
    name: 'Masks and Capes',
    artist: 'Spark Sam',
    url: '/themes/superheros/music/masksandcapes.mp3',
    duration: ''
  },
  {
    id: 'superhero-saving-the-day',
    name: 'Saving the Day',
    artist: 'Spark Sam',
    url: '/themes/superheros/music/savingtheday.mp3',
    duration: ''
  },
  {
    id: 'superhero-someone-you-know',
    name: 'Someone You Know',
    artist: 'Spark Sam',
    url: '/themes/superheros/music/someoneyouknow.mp3',
    duration: ''
  },
  {
    id: 'superhero-superpower-shine',
    name: 'Superpower Shine',
    artist: 'Spark Sam',
    url: '/themes/superheros/music/superpowershine.mp3',
    duration: ''
  },
  {
    id: 'superhero-wait-and-see',
    name: 'Wait and See',
    artist: 'Spark Sam',
    url: '/themes/superheros/music/waitandsee.mp3',
    duration: ''
  }
],

/* ───────── CARNIVAL2 THEME ──────── */
'template-carnival2Theme': [
  {
    id: 'carnival-cotton-candy-clouds',
    name: 'Cotton Candy Clouds',
    artist: 'Spark Sam',
    url: '/themes/carnival2/music/cottoncandyclouds.mp3',
    duration: ''
  },
  {
    id: 'carnival-bite-same-melody',
    name: 'Bite Same Melody',
    artist: 'Spark Sam',
    url: '/themes/carnival2/music/bitesamelody.mp3',
    duration: ''
  },
  {
    id: 'carnival-carnival-bash',
    name: 'Carnival Bash',
    artist: 'Spark Sam',
    url: '/themes/carnival2/music/carnivalbash.mp3',
    duration: ''
  },
  {
    id: 'carnival-carnival-dreams',
    name: 'Carnival Dreams',
    artist: 'Spark Sam',
    url: '/themes/carnival2/music/carnivaldreams.mp3',
    duration: ''
  },
  {
    id: 'carnival-sprinkles-fall-like-stars',
    name: 'Sprinkles Fall Like Stars',
    artist: 'Spark Sam',
    url: '/themes/carnival2/music/sprinklesfalllikestars.mp3',
    duration: ''
  },
  {
    id: 'carnival-step-right-up',
    name: 'Step Right Up',
    artist: 'Spark Sam',
    url: '/themes/carnival2/music/steprightup.mp3',
    duration: ''
  },
  {
    id: 'carnival-sun-is-shining',
    name: 'Sun Is Shining',
    artist: 'Spark Sam',
    url: '/themes/carnival2/music/sunisshining.mp3',
    duration: ''
  }
],

/* ───────── SHARK THEME ─────────── */
'template-sharkTheme': [
  {
    id: 'shark-groovy-like-a-shark',
    name: 'Groovy Like a Shark',
    artist: 'Spark Sam',
    url: '/themes/shark/music/groovylikeashark.mp3',
    duration: ''
  },
  {
    id: 'shark-jaws-on-the-floor',
    name: 'Jaws on the Floor',
    artist: 'Spark Sam',
    url: '/themes/shark/music/jawsonthefloor.mp3',
    duration: ''
  },
  {
    id: 'shark-sea-of-jubilee',
    name: 'Sea of Jubilee',
    artist: 'Spark Sam',
    url: '/themes/shark/music/seaofjubilee.mp3',
    duration: ''
  },
  {
    id: 'shark-shark-party-vibes',
    name: 'Shark Party Vibes',
    artist: 'Spark Sam',
    url: '/themes/shark/music/sharkpartyvibes.mp3',
    duration: ''
  },
  {
    id: 'shark-shark-tooth-grin',
    name: 'Shark Tooth Grin',
    artist: 'Spark Sam',
    url: '/themes/shark/music/sharktoothgrin.mp3',
    duration: ''
  },
  {
    id: 'shark-swimming-in-delight',
    name: 'Swimming in Delight',
    artist: 'Spark Sam',
    url: '/themes/shark/music/swimmingindelight.mp3',
    duration: ''
  }
],

/* ───────── SCIENCE THEME ───────── */
'template-scienceTheme': [
  {
    id: 'science-beakers-bubble',
    name: 'Beakers Bubble',
    artist: 'Spark Sam',
    url: '/themes/science/music/beakersbubble.mp3',
    duration: ''
  },
  {
    id: 'science-birthday-in-the-lab',
    name: 'Birthday in the Lab',
    artist: 'Spark Sam',
    url: '/themes/science/music/birthdayinthelab.mp3',
    duration: ''
  },
  {
    id: 'science-cosmic-parade',
    name: 'Cosmic Parade',
    artist: 'Spark Sam',
    url: '/themes/science/music/cosmicparade.mp3',
    duration: ''
  },
  {
    id: 'science-lab-coat-confetti',
    name: 'Lab Coat Confetti',
    artist: 'Spark Sam',
    url: '/themes/science/music/labcoatconfetti.mp3',
    duration: ''
  },
  {
    id: 'science-lab-is-boppin',
    name: 'Lab Is Boppin',
    artist: 'Spark Sam',
    url: '/themes/science/music/labisboppin.mp3',
    duration: ''
  },
  {
    id: 'science-light-up-the-dark',
    name: 'Light Up the Dark',
    artist: 'Spark Sam',
    url: '/themes/science/music/lightupthedark.mp3',
    duration: ''
  },
  {
    id: 'science-through-the-telescope',
    name: 'Through the Telescope',
    artist: 'Spark Sam',
    url: '/themes/science/music/throughthetelescope.mp3',
    duration: ''
  }
],

/* ───────── SPORTS THEME ────────── */
'template-sportsTheme': [
  {
    id: 'sports-crowds-alive',
    name: 'Crowds Alive',
    artist: 'Spark Sam',
    url: '/themes/sports/music/crowdsalive.mp3',
    duration: ''
  },
  {
    id: 'sports-game-day-birthday',
    name: 'Game Day Birthday',
    artist: 'Spark Sam',
    url: '/themes/sports/music/gamedaybirthday.mp3',
    duration: ''
  },
  {
    id: 'sports-jerseys-on',
    name: 'Jerseys On',
    artist: 'Spark Sam',
    url: '/themes/sports/music/jerseyson.mp3',
    duration: ''
  },
  {
    id: 'sports-streamers-like-goal-posts',
    name: 'Streamers Like Goal Posts',
    artist: 'Spark Sam',
    url: '/themes/sports/music/streamerslikegoalposts.mp3',
    duration: ''
  },
  {
    id: 'sports-throw-it-down',
    name: 'Throw It Down',
    artist: 'Spark Sam',
    url: '/themes/sports/music/throwitdown.mp3',
    duration: ''
  },
  {
    id: 'sports-touchdowns-to-slam-dunks',
    name: 'Touchdowns to Slam Dunks',
    artist: 'Spark Sam',
    url: '/themes/sports/music/touchdownstoslamdunks.mp3',
    duration: ''
  }
],

/* ───────── MOVIE THEME ─────────── */
'template-movieTheme': [
  {
    id: 'movie-galactic-echoes',
    name: 'Galactic Echoes',
    artist: 'Spark Sam',
    url: '/themes/movie/music/galacticechoes.mp3',
    duration: ''
  },
  {
    id: 'movie-lights-camera-action',
    name: 'Lights Camera Action',
    artist: 'Spark Sam',
    url: '/themes/movie/music/lightscameraaction.mp3',
    duration: ''
  },
  {
    id: 'movie-popcorn-dreams',
    name: 'Popcorn Dreams',
    artist: 'Spark Sam',
    url: '/themes/movie/music/popcorndreams.mp3',
    duration: ''
  },
  {
    id: 'movie-red-carpet-roll',
    name: 'Red Carpet Roll',
    artist: 'Spark Sam',
    url: '/themes/movie/music/redcarpetroll.mp3',
    duration: ''
  },
  {
    id: 'movie-the-grand-beginning',
    name: 'The Grand Beginning',
    artist: 'Spark Sam',
    url: '/themes/movie/music/thegrandbeginning.mp3',
    duration: ''
  }
],

/* ───────── TWO‑COOL THEME ──────── */
'template-twoCoolTheme': [
  {
    id: 'twocool-birthday-show',
    name: 'Birthday Show',
    artist: 'Spark Sam',
    url: '/themes/twocool/music/birthdayshow.mp3',
    duration: ''
  },
  {
    id: 'twocool-cake-so-sweet',
    name: 'Cake So Sweet',
    artist: 'Spark Sam',
    url: '/themes/twocool/music/cakesosweet.mp3',
    duration: ''
  },
  {
    id: 'twocool-double-the-fun',
    name: 'Double the Fun',
    artist: 'Spark Sam',
    url: '/themes/twocool/music/doublethefun.mp3',
    duration: ''
  },
  {
    id: 'twocool-glitter-and-galaxies',
    name: 'Glitter and Galaxies',
    artist: 'Spark Sam',
    url: '/themes/twocool/music/glitterandgalaxies.mp3',
    duration: ''
  },
  {
    id: 'twocool-those-sweet-vibes',
    name: 'Those Sweet Vibes',
    artist: 'Spark Sam',
    url: '/themes/twocool/music/thosesweetvibes.mp3',
    duration: ''
  },
  {
    id: 'twocool-two-cool',
    name: 'Two Cool',
    artist: 'Spark Sam',
    url: '/themes/twocool/music/twocool.mp3',
    duration: ''
  },
  {
    id: 'twocool-two-cool-bop',
    name: 'Two Cool Bop',
    artist: 'Spark Sam',
    url: '/themes/twocool/music/twocoolbop.mp3',
    duration: ''
  },
  {
    id: 'twocool-two-cool-for-my-birthday',
    name: 'Two Cool for My Birthday',
    artist: 'Spark Sam',
    url: '/themes/twocool/music/twocoolformybirthday.mp3',
    duration: ''
  }
],
/* ───────── DRAGON THEME ───────── */
'template-dragonTheme': [
  {
    id: 'dragon-fire-and-frosting',
    name: 'Fire and Frosting',
    artist: 'Spark Sam',
    url: '/themes/dragon/music/fireandfrosting.mp3',
    duration: ''
  },
  {
    id: 'dragon-dragon-fire-groove',
    name: 'Dragon Fire Groove',
    artist: 'Spark Sam',
    url: '/themes/dragon/music/dragonfiregroove.mp3',
    duration: ''
  },
  {
    id: 'dragon-dragon-king',
    name: 'Dragon King',
    artist: 'Spark Sam',
    url: '/themes/dragon/music/dragonking.mp3',
    duration: ''
  },
  {
    id: 'dragon-soar-dragon-soar',
    name: 'Soar Dragon Soar',
    artist: 'Spark Sam',
    url: '/themes/dragon/music/soardragonsoar.mp3',
    duration: ''
  },
  {
    id: 'dragon-the-gentle-dragon',
    name: 'The Gentle Dragon',
    artist: 'Spark Sam',
    url: '/themes/dragon/music/thegentledragon.mp3',
    duration: ''
  },
  {
    id: 'dragon-the-magic-dragon',
    name: 'The Magic Dragon',
    artist: 'Spark Sam',
    url: '/themes/dragon/music/themagicdragon.mp3',
    duration: ''
  }
],

/* ───────── PIRATE THEME ───────── */
'template-piratesTheme': [
  {
    id: 'pirate-drop-the-anchor',
    name: 'Drop the Anchor',
    artist: 'Spark Sam',
    url: '/themes/pirates/music/droptheanchor.mp3',
    duration: ''
  },
  {
    id: 'pirate-pirate-party',
    name: 'Pirate Party',
    artist: 'Spark Sam',
    url: '/themes/pirates/music/pirateparty.mp3',
    duration: ''
  },
  {
    id: 'pirate-punky-pirates',
    name: 'Punky Pirates',
    artist: 'Spark Sam',
    url: '/themes/pirates/music/punkypirates.mp3',
    duration: ''
  },
  {
    id: 'pirate-treasure-map-groove',
    name: 'Treasure Map Groove',
    artist: 'Spark Sam',
    url: '/themes/pirates/music/treasuremapgroove.mp3',
    duration: ''
  },
  {
    id: 'pirate-x-marks-the-spot',
    name: 'X Marks the Spot',
    artist: 'Spark Sam',
    url: '/themes/pirates/music/xmarksthespot.mp3',
    duration: ''
  }
],

/* ─────── FARM‑ANIMAL THEME ────── */
'template-farmAnimalsTheme': [
  {
    id: 'farm-barnyard-bash',
    name: 'Barnyard Bash',
    artist: 'Spark Sam',
    url: '/themes/farmanimal/music/barnyardbash.mp3',
    duration: ''
  },
  {
    id: 'farm-chicken-wearing-shades',
    name: 'Chicken Wearing Shades',
    artist: 'Spark Sam',
    url: '/themes/farmanimal/music/chickenwearingshades.mp3',
    duration: ''
  },
  {
    id: 'farm-cowbells-ringin',
    name: 'Cowbells Ringin’',
    artist: 'Spark Sam',
    url: "/themes/farmanimal/music/cowbellsringin'.mp3",
    duration: ''
  },
  {
    id: 'farm-funky-barn',
    name: 'Funky Barn',
    artist: 'Spark Sam',
    url: '/themes/farmanimal/music/funkybarn.mp3',
    duration: ''
  },
  {
    id: 'farm-haystack-king',
    name: 'Haystack King',
    artist: 'Spark Sam',
    url: '/themes/farmanimal/music/haystackking.mp3',
    duration: ''
  },
  {
    id: 'farm-oink-moo-cluck',
    name: 'Oink Moo Cluck',
    artist: 'Spark Sam',
    url: '/themes/farmanimal/music/oinkmoocluck.mp3',
    duration: ''
  },
  {
    id: 'farm-tractor-hum',
    name: 'Tractor Hum',
    artist: 'Spark Sam',
    url: '/themes/farmanimal/music/tractorhum.mp3',
    duration: ''
  }
],

/* ───────── LASER‑TAG THEME ────── */
'template-laserTagTheme': [
  {
    id: 'lasertag-battlefield-bop',
    name: 'Battlefield Bop',
    artist: 'Spark Sam',
    url: '/themes/lasertag/music/battlefieldbop.mp3',
    duration: ''
  },
  {
    id: 'lasertag-arenas-alive',
    name: 'Arenas Alive',
    artist: 'Spark Sam',
    url: '/themes/lasertag/music/arenasalive.mp3',
    duration: ''
  },
  {
    id: 'lasertag-dream-and-beam',
    name: 'Dream and Beam',
    artist: 'Spark Sam',
    url: '/themes/lasertag/music/dreamandbeam.mp3',
    duration: ''
  },
  {
    id: 'lasertag-goggles-on',
    name: 'Goggles On',
    artist: 'Spark Sam',
    url: '/themes/lasertag/music/goggleson.mp3',
    duration: ''
  },
  {
    id: 'lasertag-neon-dreams',
    name: 'Neon Dreams',
    artist: 'Spark Sam',
    url: '/themes/lasertag/music/neondreams.mp3',
    duration: ''
  },
  {
    id: 'lasertag-neon-lights-flicker',
    name: 'Neon Lights Flicker',
    artist: 'Spark Sam',
    url: '/themes/lasertag/music/neonlightsflicker.mp3',
    duration: ''
  }
],

/* ─────── MERMAID / OCEAN THEME ─────── */
'template-mermaidTheme': [
  {
    id: 'mermaid-underwater-glow',
    name: 'Underwater Glow',
    artist: 'Spark Sam',
    url: '/themes/mermaid/music/underwaterglow.mp3',
    duration: ''
  },
  {
    id: 'mermaid-oceans-glow',
    name: 'Ocean’s Glow',
    artist: 'Spark Sam',
    url: '/themes/mermaid/music/oceansglow.mp3',
    duration: ''
  },
  {
    id: 'mermaid-splash-in-the-room',
    name: 'Splash in the Room',
    artist: 'Spark Sam',
    url: '/themes/mermaid/music/splashintheroom.mp3',
    duration: ''
  },
  {
    id: 'mermaid-treasure-chest-of-laughs',
    name: 'Treasure Chest of Laughs',
    artist: 'Spark Sam',
    url: '/themes/mermaid/music/treasurechestoflaughs.mp3',
    duration: ''
  }
],
// ───────────────────── Carnival Theme ─────────────────────
'template-carnivalTheme': [
  {
    id: 'carnival-cotton-candy-clouds',
    name: 'Cotton Candy Clouds',
    artist: 'Spark Sam',
    url: '/themes/carnival2/music/cottoncandyclouds.mp3',
    duration: ''
  },
  {
    id: 'carnival-bite-same-melody',
    name: 'Bite Same Melody',
    artist: 'Spark Sam',
    url: '/themes/carnival2/music/bitesamelody.mp3',
    duration: ''
  },
  {
    id: 'carnival-carnival-bash',
    name: 'Carnival Bash',
    artist: 'Spark Sam',
    url: '/themes/carnival2/music/carnivalbash.mp3',
    duration: ''
  },
  {
    id: 'carnival-carnival-dreams',
    name: 'Carnival Dreams',
    artist: 'Spark Sam',
    url: '/themes/carnival2/music/carnivaldreams.mp3',
    duration: ''
  },
  {
    id: 'carnival-sprinkles-fall-like-stars',
    name: 'Sprinkles Fall Like Stars',
    artist: 'Spark Sam',
    url: '/themes/carnival2/music/sprinklesfalllikestars.mp3',
    duration: ''
  },
  {
    id: 'carnival-step-right-up',
    name: 'Step Right Up',
    artist: 'Spark Sam',
    url: '/themes/carnival2/music/steprightup.mp3',
    duration: ''
  },
  {
    id: 'carnival-sun-is-shining',
    name: 'Sun Is Shining',
    artist: 'Spark Sam',
    url: '/themes/carnival2/music/sunisshining.mp3',
    duration: ''
  }
],

// ──────────────────── Floral Wedding Theme ───────────────────
'template-floralweddingTheme': [
  {
    id: 'floralwedding-eternal-vows',
    name: 'Eternal Vows',
    artist: 'Sam Sparkman',
    url: '/themes/floralwedding/music/eternalvows.mp3',
    duration: ''
  }
],
// ──────────────────── Tolkien Theme ───────────────────
'template-tolkienTheme': [
  {
    id: 'tolkien-whispers-of-the-willow',
    name: 'Whispers of the Willow',
    artist: 'Sam Sparkman',
    url: '/themes/tolkien/music/whispersofthewillow.mp3',
    duration: ''
  },
  {
    id: 'tolkien-green-and-gold',
    name: 'Green and Gold',
    artist: 'Sam Sparkman',
    url: '/themes/tolkien/music/greenandgold.mp3',
    duration: ''
  },
  {
    id: 'tolkien-my-hope-my-love',
    name: 'My Hope My Love',
    artist: 'Sam Sparkman',
    url: '/themes/tolkien/music/myhopemylove.mp3',
    duration: ''
  },
  {
    id: 'tolkien-straight-through-it',
    name: 'Straight Through It',
    artist: 'Sam Sparkman',
    url: '/themes/tolkien/music/straightthroughit.mp3',
    duration: ''
  },
],
// ──────────────────── Harry Potter Theme ───────────────────
'template-harrypotterTheme': [
  {
    id: 'harrypotter-whispered-spells',
    name: 'Whispered Spells',
    artist: 'Sam Sparkman',
    url: '/themes/harrypotter/music/whisperedspells.mp3',
    duration: ''
  },
  {
    id: 'harrypotter-echoes-of-enchantment',
    name: 'Echoes of Enchantment',
    artist: 'Sam Sparkman',
    url: '/themes/harrypotter/music/echoesofenchantment.mp3',
    duration: ''
  },
  {
    id: 'harrypotter-celestial-magic',
    name: 'Celestial Magic',
    artist: 'Spark Sam',
    url: '/themes/harrypotter/music/celestialmagic.mp3',
    duration: ''
  },
  {
    id: 'harrypotter-hedwigs-ballad',
    name: 'Headwig’s Balad',
    artist: 'Sam Sparkman',
    url: '/themes/harrypotter/music/hedwigsballad.mp3',
    duration: ''
  },
],
// ──────────────────── Halloween Theme ──────────────────── 
'template-halloweenTheme': [
  {
    id: 'halloween-haunted-dance-floors',
    name: 'Haunted Dance Floors',
    artist: 'Sam Sparkman',
    url: '/themes/halloween/music/haunteddancefloors.mp3',
    duration: ''
  },
  {
    id: 'halloween-a-spooky-affair',
    name: 'A Spooky Affair',
    artist: 'Sam Sparkman',
    url: '/themes/halloween/music/aspookyaffair.mp3',
    duration: ''
  },
  {
    id: 'halloween-harmony-of-horror',
    name: 'Harmony of Horror',
    artist: 'Sam Sparkman',
    url: '/themes/halloween/music/harmonyofhorror.mp3',
    duration: ''
  },
  {
    id: 'halloween-night-of-the-living-dead',
    name: 'Night of the Living Dead',
    artist: 'Spark Sam',
    url: '/themes/halloween/music/nightofthelivingdead.mp3',
    duration: ''
  },
],
// ──────────────────── Floral Wedding Theme ───────────────────
'template-baseballTheme': [
  {
    id: 'baseball-curveball-cake',
    name: 'Curveball Cake',
    artist: 'Sam Sparkman',
    url: '/themes/baseball/music/curveballcake.mp3',
    duration: ''
  }
],
'template-werockjaxTheme': [
  {
    id: 'werockjax-the-gym-we-rock',
    name: 'The Gym We Rock',
    artist: 'Sam Sparkman',
    url: '/themes/werockjax/music/thegymwerock.mp3',
    duration: ''
  },
  {
    id: 'werockjax-party-riot',
    name: 'Party Riot',
    artist: 'Sam Sparkman',
    url: '/themes/werockjax/music/partyriot.mp3',
    duration: ''
  }
],
'template-candyadventuretheme': [
  {
    id: 'candyadventure-candy-valley',
    name: 'Candy Valley',
    artist: 'Sparky Sam',
    url: '/themes/candyadventure/music/candyvalley.mp3',
    duration: ''          // optional – leave blank
  },  
  {
      id: 'candy-candy-clouds',
      name: 'Candy Clouds',
      artist: 'Sparky Sam',
      url: '/themes/candy/music/candy-clouds.mp3',
      duration: ''          // optional – leave blank
    },
    {
      id: 'candy-electric-whimsey',
      name: 'Electric Whimsey',
      artist: 'Sparky Sam',
      url: '/themes/candy/music/electric-whimsey.mp3',
      duration: ''
    },
    {
      id: 'candy-sugar-dreams',
      name: 'Sugar Dreams',
      artist: 'Sparky Sam',
      url: '/themes/candy/music/sugar-dreams.mp3',
      duration: ''
    },
    {
      id: 'candy-sugar-rush',
      name: 'Sugar Rush',
      artist: 'Sparky Sam',
      url: '/themes/candy/music/sugar-rush.mp3',
      duration: ''
    },
    {
      id: 'candy-weve-got-the-candles',
      name: "We've Got the Candles",
      artist: 'Sparky Sam',
      url: "/themes/candy/music/we've-got-the-candles.mp3",
      duration: ''
    },
    {
        id: 'sweets-lollipop-lights',
        name: "Lollipop Lights",
        artist: 'Sparky Sam',
        url: "/themes/sweets/music/lollipoplights.mp3",
        duration: ''
    },
    {
        id: 'sweets-sprinkle-confetti',
        name: "Spinkle Confetti",
        artist: 'Sparky Sam',
        url: "/themes/sweets/music/sprinkleconfetti.mp3",
        duration: ''
    },
  ],
  'template-littletownplayTheme': [
  {
    id: 'littletownplay-little-town-tune',
    name: 'Little Town Tune',
    artist: 'Sam Sparkman',
    url: '/themes/littletownplay/music/littletowntune.mp3',
    duration: ''          // optional – leave blank
  },
  {
    id: 'littletownplay-tune-for-the-town',
    name: 'Tune for the Town',
    artist: 'Sam Sparkman',
    url: '/themes/littletownplay/music/tuneforthetown.mp3',
    duration: ''          // optional – leave blank
  },  
  ],
  'template-nascarTheme': [
  {
    id: 'nascar-spark-run-anthem',
    name: 'Spark Run Anthem',
    artist: 'Sam Sparkman',
    url: '/themes/nascar/music/sparkrunanthem.mp3',
    duration: ''          // optional – leave blank
  },
  {
    id: 'nascar-skyline-fire',
    name: 'Skyline Fire',
    artist: 'Sam Sparkman',
    url: '/themes/nascar/music/skylinefire.mp3',
    duration: ''           // optional – leave blank
  },
  {
    id: 'racecars-rev-it-up',
    name: 'Rev It Up',
    artist: 'Sparky Sam',
    url: '/themes/racecars/music/revitup.mp3',
    duration: ''
  },
  {
    id: 'racecars-speed-of-cake',
    name: 'Speed of Cake',
    artist: 'Sparky Sam',
    url: '/themes/racecars/music/speedofcake.mp3',
    duration: ''
  },
  {
    id: 'racecars-fast-lane',
    name: 'Fast Lane',
    artist: 'Sparky Sam',
    url: '/themes/racecars/music/fastlane.mp3',
    duration: ''
  }  
  ],
  'template-monstertruckTheme': [
  {
    id: 'monstertruck-big-wheels-big-dreams',
    name: 'Big Wheels, Big Dreams',
    artist: 'Sam Sparkman',
    url: '/themes/monstertruck/music/bigwheelsbigdreams.mp3',
    duration: ''          // optional – leave blank
  },
  {
    id: 'monstertruck-partys-revved',
    name: 'Partys Revved',
    artist: 'Sam Sparkman',
    url: '/themes/monstertruck/music/partysrevved.mp3',
    duration: ''          // optional – leave blank
  },
  {
    id: 'monstertruck-monster-truck-madness',
    name: 'Monster Truck Madness',
    artist: 'Sam Sparkman',
    url: '/themes/monstertruck/music/monstertruckmadness.mp3',
    duration: ''          // optional – leave blank
  },  
  ],
  'template-fromanotherworldTheme': [
  {
    id: 'fromanotherworld-from-another-world',
    name: 'From Another World',
    artist: 'Sam Sparkman',
    url: '/themes/fromanotherworld/music/fromanotherworld.mp3',
    duration: ''          // optional – leave blank
  },
  {
    id: 'fromanotherworld-celestial-currents',
    name: 'Celestial Currents',
    artist: 'Sam Sparkman',
    url: '/themes/fromanotherworld/music/celestialcurrents.mp3',
    duration: ''          // optional – leave blank
  },
  ],
  'template-chanukahTheme': [
    {
      id: 'chanukah-light-the-flame',
      name: 'Light the Flame',
      artist: 'Sam Sparkman',
      url: '/themes/chanukah/music/lighttheflame.mp3',
      duration: ''          // optional – leave blank
    },{
    id: 'chanukah-mountain-lights',
    name: 'Mountain Lights',
    artist: 'Sam Sparkman',
    url: '/themes/chanukah/music/mountainlights.mp3',
    duration: ''          // optional – leave blank
  },
  ],
  'template-christmasTheme': [
    {
      id: 'christmas-magic-and-the-soul',
      name: 'Magic and the Soul',
      artist: 'Sam Sparkman',
      url: '/themes/christmas/music/magicandthesoul.mp3',
      duration: ''          // optional – leave blank
    },{
    id: 'christmas-snowflake-serenade',
    name: 'Snowflake Serenade',
    artist: 'Sam Sparkman',
    url: '/themes/christmas/music/snowflakeserenade.mp3',
    duration: ''          // optional – leave blank
  },
  ],
  'template-thegreenplaygroundTheme': [
    {
      id: 'thegreenplayground-mango-high',
      name: 'Mango High',
      artist: 'Sam Sparkman',
      url: '/themes/thegreenplayground/music/mangohigh.mp3',
      duration: ''          // optional – leave blank
    },
  ],
  'template-princesscinematicTheme': [
    {
      id: 'princesscinematic-im-a-princess',
      name: 'I am a Princess',
      artist: 'Sam Sparkman',
      url: '/themes/princesscinematic/music/imaprincess.mp3',
      duration: ''          // optional – leave blank
    },
    {
      id: 'princesscinematic-princess-palace',
      name: 'Princess Palace',
      artist: 'Sam Sparkman',
      url: '/themes/princesscinematic/music/princesspalace.mp3',
      duration: ''          // optional – leave blank
    },
  ],
  'template-grandviewTheme': [
    {
      id: 'grandview-new-shoes-new-rules',
      name: 'New Shoes New Rules',
      artist: 'Sam Sparkman',
      url: '/themes/grandview/music/newshoesnewrules.mp3',
      duration: ''          // optional – leave blank
    },
  ],
  'template-fairytaleTemplate': [
    {
      id: 'fairytale-fairytale-tune',
      name: 'Fairytale Tune',
      artist: 'Sam Sparkman',
      url: '/themes/fairytale/music/fairytaletune.mp3',
      duration: ''          // optional – leave blank
    },
    {
      id: 'princesscinematic-im-a-princess',
      name: 'I am a Princess',
      artist: 'Sam Sparkman',
      url: '/themes/princesscinematic/music/imaprincess.mp3',
      duration: ''          // optional – leave blank
    },
    {
      id: 'princesscinematic-princess-palace',
      name: 'Princess Palace',
      artist: 'Sam Sparkman',
      url: '/themes/princesscinematic/music/princesspalace.mp3',
      duration: ''          // optional – leave blank
    },
    {
      id: 'princess-castle-of-icing',
      name: 'Castle of Icing',
      artist: 'Sparky Sam',
      url: '/themes/princess/music/castle-of-icing.mp3',
      duration: ''
    },
    {
      id: 'princess-crown-of-dreams',
      name: 'Crown of Dreams',
      artist: 'Sparky Sam',
      url: '/themes/princess/music/crown-of-dreams.mp3',
      duration: ''
    },
    {
      id: 'princess-bippity-bop',
      name: 'Bippity Bop',
      artist: 'Sparky Sam',
      url: '/themes/princess/music/bippity-bop.mp3',
      duration: ''
    },
  ],
  'template-mermaidcinematicTheme': [
    {
      id: 'mermaid-underwater-glow',
      name: 'Underwater Glow',
      artist: 'Spark Sam',
      url: '/themes/mermaid/music/underwaterglow.mp3',
      duration: ''
    },
    {
      id: 'mermaid-oceans-glow',
      name: 'Ocean’s Glow',
      artist: 'Spark Sam',
      url: '/themes/mermaid/music/oceansglow.mp3',
      duration: ''
    },
    {
      id: 'mermaid-splash-in-the-room',
      name: 'Splash in the Room',
      artist: 'Spark Sam',
      url: '/themes/mermaid/music/splashintheroom.mp3',
      duration: ''
    },
    {
      id: 'mermaid-treasure-chest-of-laughs',
      name: 'Treasure Chest of Laughs',
      artist: 'Spark Sam',
      url: '/themes/mermaid/music/treasurechestoflaughs.mp3',
      duration: ''
    }
  ],
  'template-halloweenhorrorTheme': [
    {
      id: 'halloweenhorror-im-gonna-get-you',
      name: 'I am going to get you',
      artist: 'Scary Sparkman',
      url: '/themes/halloweenhorror/music/imgonnagetyou.mp3',
      duration: ''          // optional – leave blank
    },
  ],
  'template-fallfestivalTheme': [
    {
      id: 'fallfestival-autumns-embrace',
      name: 'Autumn’s Embrace',
      artist: 'Sam Sparkman',
      url: '/themes/fallfestival/music/autumnsembrace.mp3',
      duration: ''          // optional – leave blank
    },
    {
      id: 'fallfestival-falling-for-fall',
      name: 'Falling for Fall',
      artist: 'Sam Sparkman',
      url: '/themes/fallfestival/music/fallingforfall.mp3',
      duration: ''          // optional – leave blank
    },
    {
      id: 'fallfestival-whisper-of-the-leaves',
      name: 'Whisper of the Leaves',
      artist: 'Sam Sparkman',
      url: '/themes/fallfestival/music/whisperoftheleaves.mp3',
      duration: ''          // optional – leave blank
    },
  ],
  // Add more themes here
};

const MusicSelector: React.FC<MusicSelectorProps> = ({
  templateId,
  selectedMusicId,
  onSelectMusic
}) => {
  const [playing, setPlaying] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [loadingAudio, setLoadingAudio] = useState<string | null>(null);

  // Get music options for the selected template
  const getMusicOptions = (): MusicOption[] => {
    if (!templateId) return [];
    
    // Map template IDs to music library keys
    const templateKey = `template-${templateId}`;
    return musicLibraries[templateKey] || [];
  };

  const musicOptions = getMusicOptions();

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handlePreview = async (music: MusicOption) => {
    setLoadingAudio(music.id);
    
    try {
      // If already playing this song, stop it
      if (playing === music.id && audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
        setPlaying(null);
        setLoadingAudio(null);
        return;
      }

      // Stop any currently playing audio
      if (audioRef.current) {
        audioRef.current.pause();
      }

      // Create and play new audio
      const audio = new Audio(music.url);
      audio.volume = 0.5;
      audioRef.current = audio;

      audio.onended = () => {
        setPlaying(null);
        audioRef.current = null;
      };

      await audio.play();
      setPlaying(music.id);
    } catch (error) {
      console.error('Error playing audio:', error);
      alert('Unable to preview this song. Please try again.');
    } finally {
      setLoadingAudio(null);
    }
  };

  const handleSelect = (music: MusicOption) => {
    // Stop any playing audio when selecting
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlaying(null);
    }
    onSelectMusic(music.id, music.url, music.name);
  };

  if (musicOptions.length === 0) {
    return (
      <div className="music-selector-empty">
        <p>No music available for this theme.</p>
      </div>
    );
  }

  return (
    <div className="music-selector">
      <div className="music-options">
        {musicOptions.map((music) => (
          <div
            key={music.id}
            className={`music-option ${selectedMusicId === music.id ? 'selected' : ''}`}
          >
            <div className="music-info">
              <h3 className="music-name">{music.name}</h3>
              <p className="music-artist">{music.artist}</p>
              <p className="music-duration">{music.duration}</p>
            </div>
            <div className="music-actions">
              <SparkButton
                size="small"
                variant="secondary"
                onClick={() => handlePreview(music)}
                disabled={loadingAudio === music.id}
              >
                {loadingAudio === music.id ? (
                  'Loading...'
                ) : playing === music.id ? (
                  '⏸ Stop'
                ) : (
                  '▶ Preview'
                )}
              </SparkButton>
              <SparkButton
                size="small"
                onClick={() => handleSelect(music)}
                disabled={selectedMusicId === music.id}
              >
                {selectedMusicId === music.id ? 'Selected' : 'Select'}
              </SparkButton>
            </div>
          </div>
        ))}
      </div>
      
      {/* Option to not use music */}
      <div className="no-music-option">
        <SparkButton
          variant="secondary"
          onClick={() => onSelectMusic('', '', '')}
          disabled={selectedMusicId === ''}
        >
          No Music
        </SparkButton>
      </div>
    </div>
  );
};

export default MusicSelector;